import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import useRegion from '~/composables/useRegion';
import { useCurrencyStore } from '~/store/currency';
import { getLegacyLocalePrefix } from '~/helpers/legacyLocale';

// Reimplementation of graphql/pkg/graph/model/utils.go generateOrderUrl
// But this only handles webinterface URLs except order step urls because it
// needs also server location handling which is done by the graphql backend only.
export default (givenUrl: string) => {
  const url = givenUrl.startsWith('/') ? givenUrl.slice(1) : givenUrl;
  const { region } = useRegion();
  const currencyStore = useCurrencyStore();
  const { currency } = storeToRefs(currencyStore);
  const { locale } = useI18n();
  const authStore = useAuthStore();
  const { isAuthenticated } = storeToRefs(authStore);

  return computed(() => {
    // if not logged in, we directly go to the webinterface
    if (!isAuthenticated.value) {
      return `/${region.value}/${url}`;
    }

    // Use legacy mapping to get a legacy prefix (e.g. 'de', 'us/en', 'ja')
    const legacyPrefix = getLegacyLocalePrefix(locale.value, currency.value);

    // Derive a locale token used by auth/setLocale from legacy prefix
    const legacyToLocaleToken: Record<string, string> = {
      de: 'de_DE',
      en: 'en_150',
      fr: 'fr_FR',
      it: 'it_IT',
      pl: 'pl_PL',
      es: 'es_ES',
      pt: 'pt_PT',
      nl: 'nl_NL',
      ru: 'ru_RU',
      ja: 'ja_JP',
      zh: 'zh_CN',
      gb: 'en_GB',
    };

    // Start with explicit region from useRegion if available
    let targetRegion = (region?.value || 'eur').toLowerCase();
    let localeOverride = 'en_001';

    if (legacyPrefix) {
      if (legacyPrefix.startsWith('us/')) {
        localeOverride = 'en_US';
        targetRegion = 'int';
      } else {
        const parts = legacyPrefix.split('/');
        const key = parts[parts.length - 1];
        if (key in legacyToLocaleToken) {
          // prefer locale token from mapping
          localeOverride = legacyToLocaleToken[key];
        } else if (key === 'en') {
          // default english for EUR
          localeOverride = currency.value === 'USD' ? 'en_US' : 'en_150';
        } else {
          // fallback
          localeOverride = 'en_001';
        }
      }
    }

    const endpoint = `/${targetRegion}/auth/setLocale/${localeOverride.toLowerCase()}`;
    const encodedRedirect = encodeURIComponent(`/${url}`);

    return `${endpoint}?redirectAfterLocaleSwitch=${encodedRedirect}`;
  });
};
